// The error vocabulary from the handoff README's code table. `service/classifyError` maps an
// `ApiError` onto one of these; the components render `platformActions.errors.<code>` for it, so
// adding a code here means adding one key to `i18n/en.json` (the parity spec enforces it). Orphex
// ships English only — there is no second bundle to keep in step.

export const PLATFORM_ACTION_ERROR_CODES = [
  /** 403 — workspace not whitelisted on the private feature flag. Page-level, not form-level. */
  'PLATFORM_ACTIONS_DISABLED',
  /** 403 — role below the action's required role (GET viewer / POST editor). */
  'PERMISSION_DENIED',
  /** 422 — body shape invalid; `error.fields` carries the field errors. */
  'VALIDATION_FAILED',
  /** 422 — unknown platform, campaign not found, or ambiguous campaign (pass marketing_channel). */
  'INVALID_ACTION_INPUT',
  /** 422 — `action_type` not in the backend registry. */
  'UNKNOWN_ACTION',
  /** 422 — `error.details.violations` lists the reasons. */
  'GUARDRAIL_VIOLATION',
  /** 422 — non-integer page/page_size. */
  'INVALID_PAGINATION',
  /** 409 — the confirm token no longer matches; re-propose. */
  'CONFIRM_TOKEN_INVALID',
  /** 409 — the confirm token aged past its 15-minute TTL; re-propose. */
  'CONFIRM_TOKEN_EXPIRED',
  /** 409 — a decision on a request that already moved on. */
  'INVALID_ACTION_STATE',
  /**
   * 409 — the propose re-used an `idempotency_key` that already names a request. **Resume that
   * one, never re-propose.** `error.details` carries `{request_id, status}` when the original is
   * this workspace's; a collision with another workspace's key still refuses but discloses
   * nothing, so `duplicate` is `null` there and only the message renders.
   */
  'DUPLICATE_ACTION_REQUEST',
  /**
   * 409 — undo asked on a request that never succeeded, or whose preview holds no before-value to
   * restore. **Retryable in principle**: once the original succeeds, the same undo becomes legal.
   */
  'REVERT_NOT_ELIGIBLE',
  /**
   * 422 — the action type has no inverse at all (every `*.create`). Unlike the 409 above this is a
   * PERMANENT property of the request, so the caller must not offer a retry.
   */
  'ACTION_NOT_REVERSIBLE',
  /** 404 — workspace or request not found in this workspace. */
  'NOT_FOUND',
  /**
   * 503 — eligibility could not be established: no adapter for the platform, or its live evidence
   * was unreachable. **Nothing was proposed**, so this is a plain retry, not a stale preview. It is
   * mapped rather than left to `UNKNOWN` because the envelope's message is the backend's own
   * `action eligibility is unknown: <reason_code>` — a machine string `{detail}` would render raw.
   */
  'SERVICE_UNAVAILABLE',
  /**
   * Navigation/unmount aborted the request. A control-flow signal, NOT a failure: callers drop it
   * silently, so — alone among these codes — it has no message key in the locale bundles.
   */
  'CANCELED',
  /** Anything else: transport failures, 5xx, and codes the backend adds after this build. */
  'UNKNOWN',
] as const

export type PlatformActionErrorCode = (typeof PLATFORM_ACTION_ERROR_CODES)[number]

/** Codes that render as copy — every one of these needs a `platformActions.errors.<code>` key. */
export const RENDERED_ERROR_CODES = PLATFORM_ACTION_ERROR_CODES.filter(
  (code) => code !== 'CANCELED',
)

/** The request a `DUPLICATE_ACTION_REQUEST` collided with, when it is this workspace's to name. */
export interface DuplicateActionTarget {
  requestId: string
  status: string
}

/**
 * One of the ad accounts an ambiguous `*.create` could have meant. The backend refuses rather than
 * guessing and hands back the list precisely so a surface can ask — a refusal that only said "name
 * an account" would demand an id the user was never shown.
 */
export interface AdAccountCandidate {
  adAccountId: string
  name: string
}

/**
 * What the composables render. `violations` is populated for `GUARDRAIL_VIOLATION` (read off the
 * envelope's `error.details.violations`), `fields` for `VALIDATION_FAILED`, and `duplicate` for a
 * same-workspace `DUPLICATE_ACTION_REQUEST`.
 */
export interface PlatformActionError {
  code: PlatformActionErrorCode
  /** True for the two token codes — the caller shows "preview is stale, refresh" and re-proposes. */
  requiresRepropose: boolean
  violations: string[]
  fields: Record<string, string[]>
  /** The already-existing request to resume; `null` whenever there is none to disclose. */
  duplicate: DuplicateActionTarget | null
  /**
   * Ad accounts an ambiguous create could have meant, off `error.details.candidates`. Empty for
   * every other `INVALID_ACTION_INPUT` — an unknown platform or a missing campaign carries none.
   */
  candidates: AdAccountCandidate[]
  /** The backend's human message, for the `{detail}` interpolation on an unmapped code. */
  detail: string
}

const CODE_SET = new Set<string>(PLATFORM_ACTION_ERROR_CODES)

export function isPlatformActionErrorCode(value: string): value is PlatformActionErrorCode {
  return CODE_SET.has(value)
}
